import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import LoaderComponent from '../loaderComp/LoaderComponent'
import ModalComponent from '../ModalComponent/ModalComponent'
import { getReportPackageDetails, gsspReportDownload } from '../services/fetchreport'
import { toast } from 'react-toastify'
import './PopoverGsspDownload.scss'

interface FileOption {
  id: string
  displayType: string
  fileType: string
  reportPath: string
}

interface ReportRow {
  reportId: string
  reportName: string
  reportType: string
  reportPath: string
  fileType: string
  fileOptions: FileOption[]
}

interface Props {
  cntntid: string
  gsspUserId?: string
}

function PopoverGsspDownload ({ cntntid, gsspUserId = '' }: Props) {
  const userData = useSelector((state: any) => state.reducers.userInfo)

  const [isloading, setLoading] = useState(false)
  const [reportData, setReportData] = useState<ReportRow[]>([])
  const [cognosRegion, setCognosRegion] = useState<string>('')
  const [modalOpen, setModal] = useState(true)
  const [selectedReportType, setSelectedReportType] = useState<Record<string, string>>({})
  const [successMessage, setSuccessMessage] = useState('')

  console.log('cntntid:', cntntid)

  const buildInputReportData = (response: any): ReportRow[] => {
    const items = response?.items || []
    const inputReportData: ReportRow[] = []

    items.forEach((item: any, itemIndex: number) => {
      const reports = item.reportResp || []
      console.log('Processing item:', itemIndex, 'with reports:', reports)
      console.log('reports:', reports)
      reports.forEach((report: any, reportIndex: number) => {
        setCognosRegion(report.cognosRegion || '')
        const files = report.files || []

        const fileOptions: FileOption[] = files
          .filter((file: any) => file && file.fileType && file.reportPath)
          .map((file: any, index: number) => {
            const fileType = String(file.fileType)
            const lowerType = fileType.toLowerCase()

            let displayType = fileType
            if (lowerType.includes('pdf')) {
              displayType = 'PDF'
            } else if (lowerType.includes('xls') || lowerType.includes('excel')) {
              displayType = 'Excel'
            }

            return {
              id: `${fileType}-${index}`,
              displayType,
              fileType,
              reportPath: file.reportPath
            }
          })

        const reportType =
          fileOptions.length > 0
            ? fileOptions.map(option => option.displayType).join(' or ')
            : 'Excel'

        const defaultOption = fileOptions[0] || ({} as FileOption)

        inputReportData.push({
          reportId: report.cntntid || `${itemIndex}-${reportIndex}`,
          reportName: report.reportName || '',
          reportType,
          reportPath: defaultOption.reportPath || '',
          fileType: defaultOption.fileType || '',
          fileOptions
        })
      })
    })

    return inputReportData
  }

  const fetchLargeReportData = useCallback(async () => {
    setLoading(true)

    try {
      const containerId = cntntid
      const response = await getReportPackageDetails(containerId)
      console.log('getReportPackageDetails response:', response)
      if (response && response.ok !== false) {
        const formatted = buildInputReportData(response)
        console.log('Formatted report data:', formatted)
        setReportData(formatted)
      } else {
        console.error('API error:', response)
        console.error('Error fetching report data:', response)
        setReportData([])
      }
    } catch (error) {
      console.error('Fetch error:', error)
      setReportData([])
    } finally {
      setLoading(false)
    }
  }, [cntntid])

  useEffect(() => {
    if (cntntid) {
      fetchLargeReportData()
    } else {
      console.error('Missing params:', cntntid)
    }
  }, [fetchLargeReportData, cntntid])

  const handleReportTypeChange = (reportId: string, type: string) => {
    setSuccessMessage('')
    setSelectedReportType(prev => ({
      ...prev,
      [reportId]: type
    }))
  }

  const handleDownload = async (report: ReportRow) => {
    setSuccessMessage('')
    setLoading(true)
    try {
      const availableOptions = report.fileOptions || []
      const defaultOption = availableOptions[0] || ({} as FileOption)

      const selectedType =
        selectedReportType[report.reportId] ||
        defaultOption.fileType

      const selectedOption =
        availableOptions.find(option => option.fileType === selectedType) ||
        defaultOption

      const reportPath =
        selectedOption.reportPath || report.reportPath

      const fileType =
        selectedOption.fileType || report.fileType

      console.log('Downloading with:', reportPath, fileType)

      const response = await gsspReportDownload({
        userid: userData[0]?.userName || '',
        cntntid,
        reportName: report.reportName,
        cognosRegion,
        reportPath,
        fileType
      })

      if (!response.ok) {
        let errorMessage = 'Failed to download the report. Please try again later.'
        try {
          const errorBody = await response.json()
          errorMessage = errorBody?.message || errorBody?.error || errorMessage
        } catch {
          try {
            const errorText = await response.text()
            errorMessage = errorText || errorMessage
          } catch {
            // Use the fallback when the backend response has no readable body.
          }
        }
        throw new Error(errorMessage)
      }

      const blob = await response.blob()

      let fileName = 'report.txt'
      const disposition = response.headers.get('Content-Disposition')
      console.log('Content-Disposition Header:', disposition)
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/i)
        if (match && match[1]) {
          fileName = match[1].trim()
        }
      }
      console.log('Extracted File Name:', fileName)

      if (blob.type === 'text/plain') {
        fileName = 'error.txt'
      }

      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = fileName

      document.body.appendChild(link)
      link.click()

      link.remove()
      window.URL.revokeObjectURL(url)
      setSuccessMessage('Report downloaded successfully')
      setLoading(false)
    } catch (error) {
      console.error('Download error:', error)
      setSuccessMessage('')
      setLoading(false)
      toast.error(error instanceof Error ? error.message : 'Failed to download the report. Please try again later.', {
        position: 'top-right',
        autoClose: 2250
      })
    }
  }

  const handleModalClose = () => {
    setModal(false)
  }

  const renderContent = () => {
    return (
      <div className='gssp-download-content'>
        {isloading && (
          <div className='gssp-download-loader'>
            <LoaderComponent />
          </div>
        )}

        <p className='report-download-info'>
          Select format and click download
        </p>

        <table className='large-download-table'>
          <thead>
            <tr>
              <th>Report Name</th>
              <th>Format</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {reportData.map(report => (
              <tr key={report.reportId}>
                <td>{report.reportName}</td>

                <td>
                  {(report.fileOptions || []).length > 0
                    ? (report.fileOptions || []).map((option, index) => (
                      <label
                        className='gssp-radio-option'
                        key={option.id}
                        style={index > 0 ? { marginLeft: 10 } : undefined}
                      >
                        <input
                          type='radio'
                          name={`type-${report.reportId}`}
                          checked={
                            (selectedReportType[report.reportId] ||
                              (report.fileOptions[0] && report.fileOptions[0].fileType)) ===
                            option.fileType
                          }
                          onChange={() =>
                            handleReportTypeChange(report.reportId, option.fileType)
                          }
                        />
                        {option.displayType}
                      </label>
                      ))
                    : <span>N/A</span>}
                </td>

                <td>
                  <button
                    disabled={isloading || (report.fileOptions || []).length === 0}
                    onClick={() => handleDownload(report)}
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
      {modalOpen && (
        <ModalComponent
          disableCloseButton
          darkBackdrop
          isOpen={modalOpen}
          onClose={handleModalClose}
          title='Standard Reports Downloads'
          titleClassName='gssp-popover-heading'
        >
          {userData.length > 0 && (
            <div className='large-download-gssp'>
              {renderContent()}
              {successMessage && (
                <p className='download-success-message'>
                  {successMessage}
                </p>
              )}
            </div>
          )}
        </ModalComponent>
      )}
    </>
  )
}

export default PopoverGsspDownload
